import { useEffect, useState } from "react";

/**
 * The signer's electronic signature: their name in a script font, rendered to
 * a transparent PNG. The Sign step makes it (and the initials) when the box is
 * checked; the backend stamps the signature on the signature lines and the
 * initials on every page of the agreement.
 */
export const INK = "#1a2b6d";
export const SCRIPT_FONT = "Dancing Script";
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap";

/** Loads the script font; true once it can be drawn. */
export function useScriptFont() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
    let alive = true;
    const load = document.fonts && document.fonts.load ? document.fonts.load(`600 48px "${SCRIPT_FONT}"`) : Promise.resolve();
    load.catch(() => {}).then(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, []);
  return ready;
}

/** Render text in the script font onto a transparent PNG data URL. */
export function typedPng(text, { width, height, size }) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  let px = size;
  ctx.font = `600 ${px}px "${SCRIPT_FONT}", cursive`;
  while (px > 24 && ctx.measureText(text).width > width - 40) {
    px -= 4;
    ctx.font = `600 ${px}px "${SCRIPT_FONT}", cursive`;
  }
  ctx.fillStyle = INK;
  ctx.textBaseline = "middle";
  ctx.fillText(text, 20, height / 2);
  return canvas.toDataURL("image/png");
}

export function initialsOf(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 4);
}

/** The signature and initials images for a name, as the backend expects them. */
export function signatureImages(name) {
  return {
    signature: typedPng(name, { width: 900, height: 220, size: 110 }),
    initials: typedPng(initialsOf(name), { width: 360, height: 160, size: 100 }),
  };
}
